import type { Perfil } from '../../domain/entities/Perfil';
import { crearError, registrarError, traducirErrorSupabase } from '../../domain/errores';
import { aPerfil, type FilaPerfil } from '../mappers/perfilMapper';
import { supabase } from '../supabase/cliente';

/**
 * Acceso al perfil del usuario autenticado y a la comprobación de
 * compatibilidad con el servidor.
 */
export class RepositorioPerfil {
  /**
   * Perfil del usuario que tiene la sesión abierta.
   * La función del servidor lee `auth.uid()`, así que no recibe parámetros:
   * es imposible pedir el perfil de otra persona desde aquí.
   */
  async obtenerMiPerfil(correoSesion?: string | null): Promise<Perfil> {
    const { data, error } = await supabase.rpc('v2_get_my_profile');

    if (error) throw traducirErrorSupabase(error);

    // Según la versión de PostgREST la función devuelve un objeto o un array
    // de un elemento; ambas formas circulan en producción.
    const fila = (Array.isArray(data) ? data[0] : data) as FilaPerfil | null | undefined;
    if (!fila) throw crearError('PERFIL_NO_ENCONTRADO');

    // La función del servidor no incluye los accesos a otros clientes; se
    // leen aparte para que el ámbito de la sesión los conozca. Sin esto, un
    // usuario con varios clientes concedidos sólo vería el suyo.
    const accesos = await this.accesosPropios();
    if (accesos !== null) fila.clientes_acceso = accesos;

    return aPerfil(fila, correoSesion);
  }

  /**
   * Clientes adicionales concedidos a este usuario.
   * `null` cuando no se pudieron leer: mejor quedarse con el cliente propio
   * que impedir la entrada por un dato accesorio.
   */
  private async accesosPropios(): Promise<number[] | null> {
    const { data: sesion } = await supabase.auth.getSession();
    const usuarioId = sesion.session?.user.id;
    if (!usuarioId) return null;

    const { data, error } = await supabase
      .from('perfiles_clientes_acceso')
      .select('cliente_id')
      .eq('perfil_id', usuarioId);

    if (error) {
      registrarError('perfil.accesosPropios', error);
      return null;
    }

    return ((data ?? []) as Record<string, unknown>[])
      .map((filaAcceso) => Number(filaAcceso.cliente_id))
      .filter((id) => Number.isFinite(id));
  }

  /**
   * Etiqueta de contrato del servidor.
   * Si no coincide con la que espera esta versión de la aplicación, los datos
   * podrían tener otra forma y es más seguro no dejar operar.
   */
  async obtenerEtiquetaBackend(): Promise<string | null> {
    const { data, error } = await supabase.rpc('v3_get_backend_tag');

    if (error) throw traducirErrorSupabase(error);

    const valor = Array.isArray(data) ? data[0] : data;
    if (typeof valor === 'string') return valor;
    if (valor && typeof valor === 'object') {
      const registro = valor as Record<string, unknown>;
      const etiqueta = registro.backend_tag ?? registro.tag;
      return typeof etiqueta === 'string' ? etiqueta : null;
    }
    return null;
  }

  /** Marca que el usuario ya cambió la contraseña obligatoria. */
  async limpiarBanderaCambioClave(): Promise<void> {
    const { error } = await supabase.rpc('v2_clear_my_password_change_flag');
    if (error) throw traducirErrorSupabase(error);
  }
}

export const repositorioPerfil = new RepositorioPerfil();
